import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import HttpManager from "../../services/HttpManager";
import AccountStatementCard from "../accountStatementCard";
import { Column } from "../commonTable";
import { CourseTuitionDTO } from "../../models/CourseTuitionDTO";
import { TuitionDTO } from "../../models/TuitionDTO";
import { ArticleDTO } from "../../models/ArticleDTO";
import { dinero, recargoDetalle } from "../../utils/recargo";
import useEsTelefono from "../../utils/esTelefono";
import CambiarPromocion from "../promos/cambiarPromocion";
import useCambioPromoAcceso from "../promos/useCambioPromoAcceso";
import T from "../../navigation/v2/managment/enrollmentUpdates/textosBase";
import "./estadoCuentaModal.css";

/*
	Estado de Cuenta en un modal (Actualizaciones v2, 2026-10-29): Un Horario y Una Alumna abren el estado de cuenta de la
	persona sin salir de la pantalla. Es la MISMA vista del Estado de Cuenta (tarjeta por curso con su tabla, que en el
	teléfono pasa a tarjetas) y los mismos datos (students/{id}/_courses y _articles), solo de lectura: cobrar, facturar y
	eximir siguen en la pantalla completa, que se abre con «Abrir Completo».
*/

interface Props {
	campusId: number;
	studentId: number;
	studentName?: string | null;
	/** La matrícula que se está mirando: su curso va primero. */
	enrollmentId?: number | null;
	onClose: () => void;
}

const vencida = (t: TuitionDTO) => t.isExpired && t.isDebt && (t.statusId === 0 || t.statusId === 1) && t.enrollmentStatusId !== 3 && t.enrollmentStatusId !== 4;

const columnas: Column[] = [
	{
		id: "concepto", header: T.col.concepto, accessor: "monthNumber", overrideHeaderStyle: () => ({ textAlign: "left" }),
		render: (t: TuitionDTO) => (
			<div>
				{t.monthNumber || t.title}
				{t.previousSchedule ? <div className="ecm-sub">{T.horarioAnterior}: {t.previousSchedule}</div> : null}
				{t.surcharge ? <div className="ecm-sub oro">+ recargo{recargoDetalle(t.surcharge, t.currencyCode) ? ", " + recargoDetalle(t.surcharge, t.currencyCode) : ""}</div> : null}
			</div>
		)
	},
	{ id: "vence", header: T.col.vence, accessor: "dueDate", overrideHeaderStyle: () => ({ textAlign: "left" }) },
	{
		id: "estado", header: T.col.estado, accessor: "", overrideHeaderStyle: () => ({ textAlign: "left" }),
		render: (t: TuitionDTO) => <span className={vencida(t) ? "ecm-oro" : ""}>{vencida(t) ? `Vencida, ${t.status}` : t.status}{t.progressStatus ? <div className="ecm-sub">{t.progressStatus}</div> : null}</span>
	},
	{ id: "total", header: T.col.total, accessor: "", overrideHeaderStyle: () => ({ textAlign: "right" }), overrideCellStyle: () => ({ textAlign: "right" }), render: (t: TuitionDTO) => t.subTotal?.moneyFormat },
	{ id: "pagado", header: T.col.pagado, accessor: "", overrideHeaderStyle: () => ({ textAlign: "right" }), overrideCellStyle: () => ({ textAlign: "right" }), render: (t: TuitionDTO) => t.paidAmount?.moneyFormat },
	{
		id: "pendiente", header: T.col.pendiente, accessor: "", overrideHeaderStyle: () => ({ textAlign: "right" }), overrideCellStyle: () => ({ textAlign: "right" }),
		render: (t: TuitionDTO) => t.surcharge && t.surcharge.pending > 0.009
			? <div>{t.pendingAmount?.moneyFormat}<div className="ecm-sub oro">+ {dinero(t.surcharge.pending, t.currencyCode)}</div></div> : t.pendingAmount?.moneyFormat
	},
	{ id: "factura", header: T.col.factura, accessor: "", enCabeceraEnTelefono: true, overrideHeaderStyle: () => ({ textAlign: "right" }), overrideCellStyle: () => ({ textAlign: "right" }),
		render: (t: TuitionDTO) => t.invoiceId > 0 ? String(t.invoiceId) : "—" } as Column
];

const EstadoCuentaModal: React.FC<Props> = ({ campusId, studentId, studentName, enrollmentId, onClose }) => {
	const telefono = useEsTelefono();
	const [cursos, setCursos] = useState<CourseTuitionDTO[] | null>(null);
	const [articulos, setArticulos] = useState<ArticleDTO[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [intento, setIntento] = useState<number>(0);
	// Cambiar Promoción (2026-10-31): el botón sale según `canChange` de cada matrícula; es el mismo del Estado de Cuenta completo.
	const cambioPromo = useCambioPromoAcceso(campusId, (cursos || []).filter((c) => c.enrollmentId && !(c as any).isPlatform).map((c) => c.enrollmentId));
	const [cambiando, setCambiando] = useState<CourseTuitionDTO | null>(null);

	useEffect(() => {
		let vigente = true;
		const http = HttpManager.getInstance();
		setCursos(null); setError(null);
		Promise.all([http.financesService.getStudentCourses(campusId, studentId), http.financesService.getStudentArticles(campusId, studentId).catch(() => ({ data: [] as ArticleDTO[] }))])
			.then(([c, a]) => {
				if (!vigente) { return; }
				const lista = [...(c.data || [])];
				// La matrícula que se está mirando primero, luego las activas, luego el historial
				const peso = (x: CourseTuitionDTO) => (enrollmentId && x.enrollmentId === enrollmentId ? 0 : x.enrollmentStatusId === 1 ? 1 : 2);
				lista.sort((x, y) => peso(x) - peso(y));
				setCursos(lista);
				setArticulos((a.data || []).filter((x: ArticleDTO) => x.isDebt && !x.isCancelled));
			})
			.catch((e) => {
				if (!vigente) { return; }
				const status = e?.response?.status;
				setError(status === 403 ? T.sinPermisoDinero : (e?.response?.data?.message || e?.message || "No se pudo leer el Estado de Cuenta."));
			});
		return () => { vigente = false; };
	}, [campusId, studentId, enrollmentId, intento]);

	useEffect(() => {
		const tecla = (e: KeyboardEvent) => { if (e.key === "Escape") { onClose(); } };
		document.addEventListener("keydown", tecla);
		const antes = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => { document.removeEventListener("keydown", tecla); document.body.style.overflow = antes; };
	}, [onClose]);

	return (
		<>
		<div className={"ecm-fondo" + (telefono ? " telefono" : "")} role="dialog" aria-modal="true" aria-label={T.estadoCuenta} onClick={onClose}>
			<div className={"ecm" + (telefono ? " telefono" : "")} onClick={(e) => e.stopPropagation()}>
				<div className="ecm-cab">
					<div style={{ minWidth: 0 }}>
						<span className="ecm-ceja">{T.estadoCuenta}</span>
						<h3>{studentName || "—"}</h3>
					</div>
					<div className="ecm-botones">
						<Link className="ecm-btn s" to={`/statement?alumno=${studentId}&plantel=${campusId}`} target="_blank" rel="noopener">{T.abrirEstadoCuenta}</Link>
						<button type="button" className="ecm-btn p" onClick={onClose}>{T.cerrar}</button>
					</div>
				</div>
				<div className="ecm-cuerpo">
					{error ? (
						<div className="ecm-error" role="alert"><span>{error}</span><button type="button" className="ecm-btn s" onClick={() => setIntento(intento + 1)}>{T.retry}</button></div>
					) : cursos === null ? <div className="ecm-vacio">Cargando…</div> : cursos.length === 0 && articulos.length === 0 ? <div className="ecm-vacio">{T.sinMovimientos}</div> : (
						<>
							{cursos.map((c) => (
								<div key={"c" + c.enrollmentId} className={"ecm-curso" + (enrollmentId && c.enrollmentId === enrollmentId ? " actual" : "")}>
									<AccountStatementCard columns={columnas} data={c} onCambiarPromo={cambioPromo.puede(c.enrollmentId) && !(c as any).isPlatform ? () => setCambiando(c) : undefined} />
								</div>
							))}
							{articulos.length > 0 ? (
								<div className="ecm-articulos">
									<h4>{T.articulos}</h4>
									{articulos.map((a) => (
										<div key={"a" + a.id} className="ecm-art"><span>{a.name}</span><span className="ecm-num">{a.pendingAmount?.moneyFormat}</span></div>
									))}
								</div>
							) : null}
						</>
					)}
				</div>
			</div>
		</div>
		{/* Fuera del fondo: un clic dentro de la ventana subiría por React hasta el fondo y cerraría el Estado de Cuenta. */}
		{cambiando ? (
			<CambiarPromocion campusId={campusId} studentId={studentId} enrollmentId={cambiando.enrollmentId} courseName={cambiando.courseName || ""}
				onClose={() => setCambiando(null)} onChanged={() => { cambioPromo.recargar(); setIntento((n) => n + 1); }} />
		) : null}
		</>
	);
};

export default EstadoCuentaModal;
